/**
 * Derived from svg-path-properties@0.2.2 vendored by flubber@0.4.2 (MIT).
 * Adapted for dependency-free internal ESM use in anvil2.
 */

import parse from "./parse";
import Bezier from "./bezier";
import Arc from "./arc";
import LinearPosition from "./linear";

type ParsedSegment = [string, ...number[]];
type Point = [number, number];
type PathPoint = {
  x: number;
  y: number;
};
type PathProperties = {
  x: number;
  y: number;
  tangentX: number;
  tangentY: number;
};
type PathMeasure =
  | ReturnType<typeof Bezier>
  | ReturnType<typeof Arc>
  | ReturnType<typeof LinearPosition>;

type SvgProperties = {
  (string: string): SvgProperties | null;
  getTotalLength(): number;
  getPointAtLength(length: number): PathPoint;
  getTangentAtLength(length: number): PathPoint;
  getPropertiesAtLength(length: number): PathProperties;
};

export function svgPathProperties(svgString: string) {
  let length = 0;
  const partialLengths: number[] = [];
  const functions: Array<PathMeasure | null> = [];
  let previousCurve: ReturnType<typeof Bezier> | null = null;

  const svgProperties = function svgProperties(
    string: string,
  ): SvgProperties | null {
    if (!string) {
      return null;
    }

    const parsed = parse(string) as ParsedSegment[];
    let cur: Point = [0, 0];
    let prevPoint: Point = [0, 0];
    let curve: PathMeasure;
    let ringStart: Point = [0, 0];

    for (let i = 0; i < parsed.length; i += 1) {
      if (parsed[i][0] === "M") {
        cur = [parsed[i][1], parsed[i][2]];
        ringStart = [cur[0], cur[1]];
        previousCurve = null;
        functions.push(null);
      } else if (parsed[i][0] === "m") {
        cur = [parsed[i][1] + cur[0], parsed[i][2] + cur[1]];
        ringStart = [cur[0], cur[1]];
        previousCurve = null;
        functions.push(null);
      } else if (parsed[i][0] === "L") {
        length += Math.sqrt(
          (cur[0] - parsed[i][1]) ** 2 + (cur[1] - parsed[i][2]) ** 2,
        );
        functions.push(
          LinearPosition(cur[0], parsed[i][1], cur[1], parsed[i][2]),
        );
        cur = [parsed[i][1], parsed[i][2]];
      } else if (parsed[i][0] === "l") {
        length += Math.sqrt(parsed[i][1] ** 2 + parsed[i][2] ** 2);
        functions.push(
          LinearPosition(
            cur[0],
            parsed[i][1] + cur[0],
            cur[1],
            parsed[i][2] + cur[1],
          ),
        );
        cur = [parsed[i][1] + cur[0], parsed[i][2] + cur[1]];
      } else if (parsed[i][0] === "H") {
        length += Math.abs(cur[0] - parsed[i][1]);
        functions.push(LinearPosition(cur[0], parsed[i][1], cur[1], cur[1]));
        cur[0] = parsed[i][1];
      } else if (parsed[i][0] === "h") {
        length += Math.abs(parsed[i][1]);
        functions.push(
          LinearPosition(cur[0], cur[0] + parsed[i][1], cur[1], cur[1]),
        );
        cur[0] = parsed[i][1] + cur[0];
      } else if (parsed[i][0] === "V") {
        length += Math.abs(cur[1] - parsed[i][1]);
        functions.push(LinearPosition(cur[0], cur[0], cur[1], parsed[i][1]));
        cur[1] = parsed[i][1];
      } else if (parsed[i][0] === "v") {
        length += Math.abs(parsed[i][1]);
        functions.push(
          LinearPosition(cur[0], cur[0], cur[1], cur[1] + parsed[i][1]),
        );
        cur[1] = parsed[i][1] + cur[1];
      } else if (parsed[i][0] === "z" || parsed[i][0] === "Z") {
        length += Math.sqrt(
          (ringStart[0] - cur[0]) ** 2 + (ringStart[1] - cur[1]) ** 2,
        );
        functions.push(
          LinearPosition(cur[0], ringStart[0], cur[1], ringStart[1]),
        );
        previousCurve = null;
        cur = [ringStart[0], ringStart[1]];
      } else if (parsed[i][0] === "C") {
        curve = Bezier(
          cur[0],
          cur[1],
          parsed[i][1],
          parsed[i][2],
          parsed[i][3],
          parsed[i][4],
          parsed[i][5],
          parsed[i][6],
        );
        previousCurve = curve;
        length += curve.getTotalLength();
        cur = [parsed[i][5], parsed[i][6]];
        functions.push(curve);
      } else if (parsed[i][0] === "c") {
        curve = Bezier(
          cur[0],
          cur[1],
          cur[0] + parsed[i][1],
          cur[1] + parsed[i][2],
          cur[0] + parsed[i][3],
          cur[1] + parsed[i][4],
          cur[0] + parsed[i][5],
          cur[1] + parsed[i][6],
        );
        previousCurve = curve;
        length += curve.getTotalLength();
        cur = [parsed[i][5] + cur[0], parsed[i][6] + cur[1]];
        functions.push(curve);
      } else if (parsed[i][0] === "S") {
        if (i > 0 && ["C", "c", "S", "s"].includes(parsed[i - 1][0])) {
          const previousSegment = parsed[i - 1]!;
          const reflectedControlX =
            2 * cur[0] -
            (previousSegment[previousSegment.length - 4] as number);
          const reflectedControlY =
            2 * cur[1] -
            (previousSegment[previousSegment.length - 3] as number);

          curve = Bezier(
            cur[0],
            cur[1],
            reflectedControlX,
            reflectedControlY,
            parsed[i][1],
            parsed[i][2],
            parsed[i][3],
            parsed[i][4],
          );
        } else {
          curve = Bezier(
            cur[0],
            cur[1],
            cur[0],
            cur[1],
            parsed[i][1],
            parsed[i][2],
            parsed[i][3],
            parsed[i][4],
          );
        }

        previousCurve = curve;
        length += curve.getTotalLength();
        cur = [parsed[i][3], parsed[i][4]];
        functions.push(curve);
      } else if (parsed[i][0] === "s") {
        if (
          i > 0 &&
          ["C", "c", "S", "s"].includes(parsed[i - 1][0]) &&
          previousCurve
        ) {
          curve = Bezier(
            cur[0],
            cur[1],
            cur[0] + previousCurve.d.x - previousCurve.c.x,
            cur[1] + previousCurve.d.y - previousCurve.c.y,
            cur[0] + parsed[i][1],
            cur[1] + parsed[i][2],
            cur[0] + parsed[i][3],
            cur[1] + parsed[i][4],
          );
        } else {
          curve = Bezier(
            cur[0],
            cur[1],
            cur[0],
            cur[1],
            cur[0] + parsed[i][1],
            cur[1] + parsed[i][2],
            cur[0] + parsed[i][3],
            cur[1] + parsed[i][4],
          );
        }

        previousCurve = curve;
        length += curve.getTotalLength();
        cur = [parsed[i][3] + cur[0], parsed[i][4] + cur[1]];
        functions.push(curve);
      } else if (parsed[i][0] === "Q") {
        if (!(cur[0] === parsed[i][1] && cur[1] === parsed[i][2])) {
          curve = Bezier(
            cur[0],
            cur[1],
            parsed[i][1],
            parsed[i][2],
            parsed[i][3],
            parsed[i][4],
          );
          previousCurve = curve;
        } else {
          curve = LinearPosition(
            parsed[i][1],
            parsed[i][3],
            parsed[i][2],
            parsed[i][4],
          );
          previousCurve = null;
        }

        length += curve.getTotalLength();
        functions.push(curve);
        cur = [parsed[i][3], parsed[i][4]];
        prevPoint = [parsed[i][1], parsed[i][2]];
      } else if (parsed[i][0] === "q") {
        if (!(parsed[i][1] === 0 && parsed[i][2] === 0)) {
          curve = Bezier(
            cur[0],
            cur[1],
            cur[0] + parsed[i][1],
            cur[1] + parsed[i][2],
            cur[0] + parsed[i][3],
            cur[1] + parsed[i][4],
          );
          previousCurve = curve;
        } else {
          curve = LinearPosition(
            cur[0] + parsed[i][1],
            cur[0] + parsed[i][3],
            cur[1] + parsed[i][2],
            cur[1] + parsed[i][4],
          );
          previousCurve = null;
        }

        length += curve.getTotalLength();
        prevPoint = [cur[0] + parsed[i][1], cur[1] + parsed[i][2]];
        cur = [parsed[i][3] + cur[0], parsed[i][4] + cur[1]];
        functions.push(curve);
      } else if (parsed[i][0] === "T") {
        if (i > 0 && ["Q", "q", "T", "t"].includes(parsed[i - 1][0])) {
          curve = Bezier(
            cur[0],
            cur[1],
            2 * cur[0] - prevPoint[0],
            2 * cur[1] - prevPoint[1],
            parsed[i][1],
            parsed[i][2],
          );
          previousCurve = curve;
        } else {
          curve = LinearPosition(cur[0], parsed[i][1], cur[1], parsed[i][2]);
          previousCurve = null;
        }

        functions.push(curve);
        length += curve.getTotalLength();
        prevPoint = [2 * cur[0] - prevPoint[0], 2 * cur[1] - prevPoint[1]];
        cur = [parsed[i][1], parsed[i][2]];
      } else if (parsed[i][0] === "t") {
        if (i > 0 && ["Q", "q", "T", "t"].includes(parsed[i - 1][0])) {
          curve = Bezier(
            cur[0],
            cur[1],
            2 * cur[0] - prevPoint[0],
            2 * cur[1] - prevPoint[1],
            cur[0] + parsed[i][1],
            cur[1] + parsed[i][2],
          );
          previousCurve = curve;
        } else {
          curve = LinearPosition(
            cur[0],
            cur[0] + parsed[i][1],
            cur[1],
            cur[1] + parsed[i][2],
          );
          previousCurve = null;
        }

        length += curve.getTotalLength();
        prevPoint = [2 * cur[0] - prevPoint[0], 2 * cur[1] - prevPoint[1]];
        cur = [parsed[i][1] + cur[0], parsed[i][2] + cur[1]];
        functions.push(curve);
      } else if (parsed[i][0] === "A") {
        curve = Arc(
          cur[0],
          cur[1],
          parsed[i][1],
          parsed[i][2],
          parsed[i][3],
          parsed[i][4],
          parsed[i][5],
          parsed[i][6],
          parsed[i][7],
        );
        previousCurve = null;
        length += curve.getTotalLength();
        cur = [parsed[i][6], parsed[i][7]];
        functions.push(curve);
      } else if (parsed[i][0] === "a") {
        curve = Arc(
          cur[0],
          cur[1],
          parsed[i][1],
          parsed[i][2],
          parsed[i][3],
          parsed[i][4],
          parsed[i][5],
          cur[0] + parsed[i][6],
          cur[1] + parsed[i][7],
        );
        previousCurve = null;
        length += curve.getTotalLength();
        cur = [cur[0] + parsed[i][6], cur[1] + parsed[i][7]];
        functions.push(curve);
      }

      partialLengths.push(length);
    }

    return svgProperties;
  };

  svgProperties.getTotalLength = function () {
    return length;
  };

  svgProperties.getPointAtLength = function (fractionLength: number) {
    const fractionPart = getPartAtLength(fractionLength);
    return functions[fractionPart.i]!.getPointAtLength(fractionPart.fraction);
  };

  svgProperties.getTangentAtLength = function (fractionLength: number) {
    const fractionPart = getPartAtLength(fractionLength);
    return functions[fractionPart.i]!.getTangentAtLength(fractionPart.fraction);
  };

  svgProperties.getPropertiesAtLength = function (fractionLength: number) {
    const fractionPart = getPartAtLength(fractionLength);
    return functions[fractionPart.i]!.getPropertiesAtLength(
      fractionPart.fraction,
    );
  };

  function getPartAtLength(fractionLength: number) {
    if (fractionLength < 0) {
      fractionLength = 0;
    } else if (fractionLength > length) {
      fractionLength = length;
    }

    let index = partialLengths.length - 1;

    while (
      partialLengths[index] >= fractionLength &&
      partialLengths[index] > 0
    ) {
      index -= 1;
    }

    index += 1;

    return {
      fraction: fractionLength - (partialLengths[index - 1] ?? 0),
      i: index,
    };
  }

  return svgProperties(svgString);
}

export default svgPathProperties;
